/**
 * useCoaching Composable
 *
 * Frontend foundation for the coaching feature (Stories F-S1 + F-S2).
 *
 * Wraps the Nexus GraphQL coaching operations behind a thin, typed API:
 *   - persona scheduling (load / set / connect Calendly / disconnect)
 *   - program coaching policy (load / set)
 *   - participant coaching status + sessions (load)
 *
 * Plus two pure helpers used by the UI to decide how to render a booking
 * surface without a round-trip:
 *   - detectProvider(url)  → which scheduling provider a raw URL points at
 *   - tierFor(scheduling)  → which booking tier a PersonaScheduling resolves to
 *
 * Follows the same client-access pattern as useAIPMOnboarding: grab the Nexus
 * Apollo client via useNexusClient() (which must run inside a Vue setup
 * context) and issue inline gql operations against it.
 */

import { useNexusClient } from '@/composables/useApolloClients';
import {
  PERSONA_SCHEDULING_QUERY,
  PROGRAM_COACHING_POLICY_QUERY,
  MY_COACHING_SESSIONS_QUERY,
  COACHING_STATUS_FOR_PARTICIPANT_QUERY,
  CALENDLY_EVENT_TYPES_QUERY,
  ACKNOWLEDGE_COACHING_BOOKING_MUTATION,
  SET_PERSONA_SCHEDULING_MUTATION,
  CONNECT_CALENDLY_MUTATION,
  DISCONNECT_SCHEDULING_MUTATION,
  SET_COACHING_EVENT_TYPE_MUTATION,
  SET_PROGRAM_COACHING_POLICY_MUTATION,
  type PersonaScheduling,
  type SetPersonaSchedulingInput,
  type ProgramCoachingPolicy,
  type SetProgramCoachingPolicyInput,
  type CoachingSession,
  type CoachingStatusForParticipant,
  type CalendlyEventType,
  type SchedulingProvider,
  type SchedulingMode
} from '@/services/graphql/nexus/coaching';

// ============================================================
// Pure helpers (no Apollo / no component context required)
// ============================================================

/**
 * Detect the scheduling provider from a raw booking URL by its hostname.
 *
 * Returns one of the backend-valid provider keys ('calendly' | 'cal' | 'google'
 * | 'custom'); anything unrecognized or unparseable maps to 'custom'. This is
 * the value sent as SetPersonaSchedulingInput.provider. Never throws.
 */
export function detectProvider(url: string | null | undefined): SchedulingProvider {
  if (!url) return 'custom';

  let host = '';
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    // Not a fully-qualified URL — fall back to a loose substring match.
    host = String(url).toLowerCase();
  }

  if (/(^|\.)calendly\.com$/.test(host) || host.includes('calendly')) return 'calendly';
  if (/(^|\.)cal\.com$/.test(host) || host.includes('cal.com')) return 'cal';
  if (
    /(^|\.)calendar\.google\.com$/.test(host) ||
    /(^|\.)calendar\.app\.google$/.test(host) ||
    host.includes('google')
  ) {
    return 'google';
  }

  // Acuity and any other booking host fall through to the generic 'custom' tier.
  return 'custom';
}

/**
 * Resolve which booking tier a PersonaScheduling renders as.
 *
 *   'connected' — Calendly OAuth, prefill+lock, official widget
 *   'link'      — a raw scheduling URL (iframe / external button)
 *   'none'      — nothing bookable
 *
 * Mirrors the backend `mode` but is defensive: a 'connected' record without a
 * live connection or schedulingUrl degrades to 'link' (if a bookingUrl exists)
 * or 'none'; a disabled record is always 'none'.
 */
export function tierFor(
  scheduling: PersonaScheduling | null | undefined
): 'connected' | 'link' | 'none' {
  if (!scheduling || scheduling.enabled === false) return 'none';

  if (
    scheduling.mode === 'connected' &&
    // Backend stores connection.status as 'active' (also tolerate 'connected').
    ['connected', 'active'].includes(scheduling.connectionStatus || '') &&
    scheduling.schedulingUrl
  ) {
    return 'connected';
  }

  if (scheduling.bookingUrl || scheduling.schedulingUrl) return 'link';

  return 'none';
}

// ============================================================
// Composable
// ============================================================

export function useCoaching(providedClient?: ReturnType<typeof useNexusClient>) {
  // Accept a pre-resolved client so async callers (useAIPMOnboarding.getCoaching runs after
  // awaits, outside component setup) can pass a synchronously-cached client. Resolving fresh
  // via useNexusClient() there throws "Apollo client with id nexus not found".
  const client = providedClient ?? useNexusClient();

  // ---- Persona scheduling ----

  async function loadPersonaScheduling(personaId: string): Promise<PersonaScheduling | null> {
    const { data } = await client.query({
      query: PERSONA_SCHEDULING_QUERY,
      variables: { personaId },
      fetchPolicy: 'network-only'
    });
    return data?.personaScheduling ?? null;
  }

  async function setPersonaScheduling(
    input: SetPersonaSchedulingInput
  ): Promise<PersonaScheduling | null> {
    const { data } = await client.mutate({
      mutation: SET_PERSONA_SCHEDULING_MUTATION,
      variables: { input }
    });
    return data?.setPersonaScheduling ?? null;
  }

  async function connectCalendly(personaId: string): Promise<PersonaScheduling | null> {
    const { data } = await client.mutate({
      mutation: CONNECT_CALENDLY_MUTATION,
      variables: { personaId }
    });
    return data?.connectCalendly ?? null;
  }

  async function disconnectScheduling(personaId: string): Promise<PersonaScheduling | null> {
    const { data } = await client.mutate({
      mutation: DISCONNECT_SCHEDULING_MUTATION,
      variables: { personaId }
    });
    return data?.disconnectScheduling ?? null;
  }

  /**
   * List the Calendly event types (scheduling links) for a connected persona.
   * Used by the editor to let the creator pick which link participants book via.
   * Returns [] when the persona has no connected Calendly account.
   */
  async function loadCalendlyEventTypes(personaId: string): Promise<CalendlyEventType[]> {
    const { data } = await client.query({
      query: CALENDLY_EVENT_TYPES_QUERY,
      variables: { personaId },
      fetchPolicy: 'network-only'
    });
    return data?.coachingEventTypes ?? [];
  }

  /**
   * Set the persona's coaching booking link to a chosen Calendly event type.
   * Returns the updated PersonaScheduling (with the new schedulingUrl).
   */
  async function setCoachingEventType(
    personaId: string,
    eventTypeUri: string,
    schedulingUrl: string
  ): Promise<PersonaScheduling | null> {
    const { data } = await client.mutate({
      mutation: SET_COACHING_EVENT_TYPE_MUTATION,
      variables: { personaId, eventTypeUri, schedulingUrl }
    });
    return data?.setCoachingEventType ?? null;
  }

  // ---- Program coaching policy ----

  async function loadProgramCoachingPolicy(
    programId: string
  ): Promise<ProgramCoachingPolicy | null> {
    const { data } = await client.query({
      query: PROGRAM_COACHING_POLICY_QUERY,
      variables: { programId },
      fetchPolicy: 'network-only'
    });
    return data?.programCoachingPolicy ?? null;
  }

  async function setProgramCoachingPolicy(
    input: SetProgramCoachingPolicyInput
  ): Promise<ProgramCoachingPolicy | null> {
    const { data } = await client.mutate({
      mutation: SET_PROGRAM_COACHING_POLICY_MUTATION,
      variables: { input }
    });
    return data?.setProgramCoachingPolicy ?? null;
  }

  // ---- Participant coaching status + sessions ----

  async function loadCoachingStatus(
    programId: string
  ): Promise<CoachingStatusForParticipant | null> {
    const { data } = await client.query({
      query: COACHING_STATUS_FOR_PARTICIPANT_QUERY,
      variables: { programId },
      fetchPolicy: 'network-only'
    });
    return data?.coachingStatusForParticipant ?? null;
  }

  async function loadMyCoachingSessions(): Promise<CoachingSession[]> {
    const { data } = await client.query({
      query: MY_COACHING_SESSIONS_QUERY,
      fetchPolicy: 'network-only'
    });
    return data?.myCoachingSessions ?? [];
  }

  /**
   * Durably record that the participant has booked / self-attested their required
   * 1:1 for a program (Program-Modules coaching fix A). Stamps
   * Student.aipm.coachingAcknowledgedAt server-side so `satisfied` reads true even
   * with no CoachingSession (link / self-attest tier) — the required coaching then
   * stops re-prompting. Idempotent. Best-effort: swallows errors so a failing
   * backend never blocks the booking UX. Returns the acknowledged ISO time, or null.
   */
  async function acknowledgeCoachingBooking(programId: string): Promise<string | null> {
    if (!programId) return null;
    try {
      const { data } = await client.mutate({
        mutation: ACKNOWLEDGE_COACHING_BOOKING_MUTATION,
        variables: { programId }
      });
      return (data?.acknowledgeCoachingBooking as string) ?? null;
    } catch (error) {
      // Non-fatal: the local booking still proceeds; the flag can be re-stamped later.
      // eslint-disable-next-line no-console
      console.warn('[useCoaching] acknowledgeCoachingBooking failed:', error);
      return null;
    }
  }

  return {
    // Persona scheduling
    loadPersonaScheduling,
    setPersonaScheduling,
    connectCalendly,
    disconnectScheduling,
    loadCalendlyEventTypes,
    setCoachingEventType,

    // Program coaching policy
    loadProgramCoachingPolicy,
    setProgramCoachingPolicy,

    // Participant status + sessions
    loadCoachingStatus,
    loadMyCoachingSessions,
    acknowledgeCoachingBooking,

    // Pure helpers
    detectProvider,
    tierFor
  };
}

export type { SchedulingMode, CalendlyEventType };

export default useCoaching;
